<template>
  <NewBsBox
    :bs="menuBoxBs"
    class="menu-box"
  >
    <template v-if="showBannerExpand">
      <!--卡片模式说着通栏模式的展开状态-->
      <MenuFlatList
        :menu-flat-list="menuFlatList"
        :toggle-expand="toggleExpand"
        :get-tree-node-bs="getTreeNodeBs"
        :active-id="activeId"
        :id-key="idKey"
      >
        <template
          #text="{ treeNode }"
        >
          <slot
            :treeNode="treeNode"
            name="title"
          >
            {{ treeNode.text }}
          </slot>
        </template>
      </MenuFlatList>
      <NewBsBox v-if="isCollapsible">
        <slot
          name="tail"
          :collapsed="false"
          :itemBs="menuFoldIconBs"
        />
        <Divider
          hair-line
          data-style="divider"
        />
        <NewBsBox
          :bs="menuFoldIconBs"
          @click="handleFold()"
        >
          <svg
            width="16"
            data-style="icon"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 2h12M8 10h6M8 6h6"
              stroke-linecap="round"
            />
            <path
              d="M2 8l2.667-2.667v5.334L2 8z"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2 14h12"
              stroke-linecap="round"
            />
          </svg>
        </NewBsBox>
      </NewBsBox>
    </template>
    <template v-else>
      <CollapsedMenuList
        :menu-flat-list="menuFlatList"
        :get-tree-node-bs="getTreeNodeBs"
        :active-id="activeId"
        :id-key="idKey"
        :modifier="modifier"
        @setItemHover="setItemHover"
      />
      <NewBsBox
        :bs="{
          bottom: 0,
        }"
      >
        <slot
          name="tail"
          :collapsed="true"
          :itemBs="menuFoldIconBs"
        />
        <Divider
          hair-line
          data-style="divider"
        />
        <NewBsBox
          :bs="menuFoldIconBs"
          @click="handleUnfold()"
        >
          <svg
            width="16"
            data-style="icon"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 2h12M7 10h7M7 6h7"
              stroke-linecap="round"
            />
            <path
              d="M4.667 8L2 5.333v5.334L4.667 8z"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2 14h12"
              stroke-linecap="round"
            />
          </svg>
        </NewBsBox>
      </NewBsBox>
      <CollapsedHoverCard
        v-if="itemHover"
        :menu-flat-list="menuFlatList"
        :active-id="activeId"
        :id-key="idKey"
        :modifier="modifier"
        :item-hover="itemHover"
        :handle-click="dealClickEvent"
        @setItemHover="setItemHover"
        @retainItemHover="retainItemHover"
      />
    </template>
  </NewBsBox>
</template>
<script lang="ts">
/* eslint-disable @typescript-eslint/no-empty-function */
  import { defineComponent, PropType } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import Divider from '@xhs/yam-beer/cmp/common/Divider/Divider.vue'
  import {
    treeToFlatList,
    nodeType,
    itemType,
  } from '@xhs/yam-beer/utils/transTree'
  import { includes, isEmpty } from 'lodash'

  import { BeerStyleObj, PromiseFn } from '../../../core'
  import { initStyle, menuItemTextBase, menuFoldIconBase } from './style'
  import MenuFlatList from './MenuFlatList.vue'
  import CollapsedMenuList from './CollapsedMenuList.vue'
  import CollapsedHoverCard from './CollapsedHoverCard.vue'

  initStyle()

  function isArrEqual(arr1: nodeType[], arr2: nodeType[]) {
    return arr1.every((item, idx) => arr2[idx] === item)
  }

  type ExpanedMethod = (item: itemType | { level: number }) => boolean

  interface MenuData {
    menuFlatList: nodeType[]
    indent: number
    itemHover?: any
    itemTextHoverId?: string
    showBannerExpand: boolean
    timeout?: NodeJS.Timeout
  }

  interface cbParamType {
    node: nodeType
    index: number
    flatList: nodeType[]
  }

  export default defineComponent({
    name: 'BeerMenu',
    components: {
      NewBsBox,
      Divider,
      MenuFlatList,
      CollapsedMenuList,
      CollapsedHoverCard,
    },
    props: {
      /**
       * 树状节点数组list，list=[{
       * id: 1, //必须
       * title: '节点一', //必须
       * groupName: 'group1', //可选， 注意：有groupName时title会失效
       * children: [...] // 可选，有嵌套层级时
       * }]
       */
      list: {
        type: Array as PropType<itemType[]>,
        required: true,
        default: () => ([]),
      },
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * String | Number
       */
      activeId: [String, Number],
      /**
       * 设置初始展开的菜单，参数是 传入的 list item & { level: number }
       */
      initExpanedOn: {
        type: Function as PropType<ExpanedMethod>,
        required: false,
      },
      /**
       * @deprecated
       *
       * 使用 initExpanedOn
       * 默认是否全部展开
       */
      defaultExpandAll: {
        type: Boolean,
        default: false,
      },
      /**
       * primary | success | danger | warning | secondary
       */
      modifier: {
        type: String,
        default: 'primary',
      },
      /**
       * 点击menu节点回调
       * 回调参数({node, index, flatList})
       * {点击的当前节点，节点索引下标，展平后的tree数组}
       * 同时提供@on-click使用，
       * @on-click参数({noode, index, flatList})
       */
      handleClick: {
        type: Function,
        default: () => () => {},
      },
      /**
       * promise类型，在then之后才会
       * 调用handlMenuClick和emit on-click事件
       */
      beforeMenuClickAsync: {
        type: Function as PropType<PromiseFn>,
        default: () => () => {},
        required: false,
      },
      /**
       * 是否互斥 toggle
       */
      mutualExclude: {
        type: Boolean,
        default: false,
      },
      /**
       * 展示节点 内容的key
       */
      textKey: {
        type: String,
        default: 'text',
      },
      /**
       * 节点唯一识别
       */
      idKey: {
        type: String,
        default: 'id',
      },
      childrenKey: {
        type: String,
        default: 'children',
      },
      stateless: Boolean,
      getNodeBs: {
        type: Function,
        default: () => ({}),
      },
      /**
       * 默认显示menu最下方的展开/收起按钮
       */
      isCollapsible: {
        type: Boolean,
        default: false,
      },
      /**
       * 自定义通栏菜单收起的事件
       */
      handleCollapse: {
        type: Function,
        default: () => () => {},
      },
    },
    emits: ['on-click', 'update:activeId'],
    data() {
      const data: MenuData = {
        menuFlatList: [],
        indent: 14, // 子节点相对父节点的缩进
        itemHover: undefined,
        itemTextHoverId: undefined,
        showBannerExpand: true,
        timeout: undefined,
      }
      return data
    },
    computed: {
      menuBoxBs(): BeerStyleObj {
        return {
          'cmp.menu.box': 'default',
          ...this.bs,
          ...(this.showBannerExpand ? {} : { width: 48 }),
        }
      },
      menuItemBs() {
        return {
          'cmp.menu': 'item',
        }
      },
      textBs() {
        return menuItemTextBase
      },
      textGroupNameBs() {
        return {
          ...menuItemTextBase,
          color: '#d9d9d9',
        }
      },
      listLen(): number {
        return this.menuFlatList.length
      },

      menuFoldIconBs() {
        return {
          ...menuFoldIconBase,
        }
      },
      bannnerMenuBs(): BeerStyleObj {
        return {
          display: 'flex',
          justifyContent: 'left',
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          maxHeight: '100%',
          ...this.bs,
        }
      },
    },
    watch: {
      list: {
        immediate: true,
        handler() {
          this.init()
        },
      },
    },
    methods: {
      hideMenu() {
        this.showBannerExpand = false
      },
      showTree(treeNode: nodeType) {
        return !isEmpty(this.itemHover) && includes(treeNode.idPath, this.itemHover.key)
      },
      getTreeNodeBs(treeNode: nodeType) {
        const { idPath } = treeNode
        const baseIndent = 16
        const indent = (idPath.length - 1) * this.indent + baseIndent
        let mb = 0
        if (treeNode.isLastItem && !treeNode.expand) {
          mb = 8
        }
        const sticky : BeerStyleObj = {}
        if (!treeNode.isLeaf) {
          sticky.position = 'sticky'
          sticky.top = 0
          sticky.bg = 'white'
          sticky.zIndex = 2
        }
        return {
          ...this.menuItemBs,
          ...sticky,
          paddingLeft: indent,
          mb,
          ...this.getNodeBs(treeNode),
        }
      },

      updateActiveId(treeNode: nodeType = { idPath: [] }) {
        if (treeNode.isLeaf) {
          this.$emit('update:activeId', treeNode[this.idKey])
        }
      },
      dealClickEvent(treeNode: nodeType, idx: number) {
        const cbParams: cbParamType = {
          node: treeNode, index: idx, flatList: this.menuFlatList,
        }
        if (this.beforeMenuClickAsync) {
          const cb = this.beforeMenuClickAsync(cbParams)
          const thenType = typeof cb.then
          if (thenType === 'function') {
            cb.then(() => {
              this.fireClick(cbParams, treeNode)
            })
            return
          }
        }
        this.fireClick(cbParams, treeNode)
      },
      fireClick(cbParams: cbParamType, treeNode: nodeType) {
        if (this.stateless || treeNode.groupName) {
          return
        }
        this.handleClick(cbParams)
        this.$emit('on-click', cbParams)
        this.updateActiveId(treeNode)
      },
      toggleExpand(treeNode: nodeType, idx: number, isClick: boolean) {
        if (this.stateless) {
          return
        }

        if (isClick) this.dealClickEvent(treeNode, idx)
        if (treeNode.isLeaf) {
          return // 叶子节点直接return
        }
        // eslint-disable-next-line no-param-reassign
        treeNode.expand = !treeNode.expand
        const parentIdPath = treeNode.idPath
        const startI = this.mutualExclude ? 0 : idx + 1

        for (let i = startI; i < this.listLen; i += 1) {
          const childNode = this.menuFlatList[i]
          const childBelongIdPath = childNode.idPath.slice(0, parentIdPath.length)
          if (i !== idx) {
            const condition = !treeNode.expand
              ? isArrEqual(childBelongIdPath, parentIdPath) // 折叠是全部子节点折叠
              : childNode.parentId === treeNode[this.idKey] // 展开是直接子节点（不包括孙子节点）展开
            if (condition) { // 折叠展开只处理当前父节点下的所有子节点（包括孙子节点）
              childNode.visible = treeNode.expand
              childNode.expand = !treeNode.hasChild && treeNode.expand
            } else if (this.mutualExclude && treeNode.expand) { // 互斥折叠处理
              childNode.expand = false
              childNode.visible = false
            }
          // 非当前父节点下的子节点无需遍历
          }
        }
      },
      findParentIndex(item: nodeType) {
        return this.menuFlatList.findIndex((treeNode: nodeType) => treeNode[this.idKey] === item.parentId)
      },
      findAndActive(itemIndex: any) {
        const item = this.menuFlatList[itemIndex]
        if (item?.parentId) {
          const parentIndex = this.findParentIndex(item)
          if (parentIndex > -1) {
            this.findAndActive(parentIndex)
          }
        }
        if (itemIndex > -1 && !item.isLeaf && !item.expand) {
          this.toggleExpand(item, itemIndex, false)
        }
      },
      expandWithDefaultActiveId() {
        const targetId = this.menuFlatList.findIndex(
          treeNode => treeNode[this.idKey] === this.activeId,
        ) || ({} as nodeType)
        this.findAndActive(targetId)
      },
      /**
       * 获取展平tree之后的list
       * @public
       * @returns {array} 展平tree之后的list
       */
      getFlatList() {
        return this.menuFlatList
      },
      init() {
        const vm = this
        function CompatExpanedAll(item: itemType): boolean {
          if (item.isLeaf) {
            return false
          }
          return vm.defaultExpandAll
        }
        const expandMethod: ExpanedMethod = this.initExpanedOn || CompatExpanedAll
        this.menuFlatList = treeToFlatList(this.list, (item: itemType) => {
          const expand = expandMethod(item)
          if (expand && item.children?.length > 0) {
            item.children.forEach(i => {
              i.visible = true
            })
          }
          return {
            expand,
            visible: expand || item.visible,
            text: item[this.textKey],
            idPath: [],
          }
        }, { idKey: this.idKey, childrenKey: this.childrenKey })
        this.expandWithDefaultActiveId()
        this.menuFlatList.forEach(treeNode => {
          if (!treeNode.parentId && !treeNode.icon) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn(`${treeNode.text} 的icon未设定`) // eslint-disable-line
            }
            // eslint-disable-next-line no-param-reassign
            treeNode.icon = 'sort'
          }
        })
      },

      handleFold() {
        this.hideMenu()
        this.handleCollapse()
      },
      handleUnfold() {
        this.showBannerExpand = true
        this.itemHover = undefined
        this.handleCollapse()
      },
      setItemHover(val : nodeType = { idPath: [] }) {
        if (!val.key) { // mouseleave
          this.timeout = setTimeout(() => {
            this.itemHover = undefined
          }, 300)
        } else {
          if (this.timeout) {
            clearTimeout(this.timeout)
          }
          this.itemHover = val
        }
      },
      retainItemHover() {
        if (this.timeout) {
          clearTimeout(this.timeout)
        }
      },
    },
  })
</script>
